import React, { useEffect, useRef } from "react";
import { AnimatePresence, motion, useAnimationControls, useReducedMotion } from "framer-motion";
import { ArrowLeftIcon } from "lucide-react";
import { wizardSteps } from "../../data/wizard";
import { APPLY_ARRIVED_EVENT, APPLY_SECTION_ID, type ApplyArrivedDetail } from "../../utils/scroll";
import { CTA_MICROCOPY } from "../../utils/buttonStyles";
import { SectionHeader } from "../SectionHeader";
import { TOTAL_STEPS, useApplicationWizard } from "./useApplicationWizard";
import { WizardContactForm } from "./WizardContactForm";
import { WizardOptionStep } from "./WizardOptionStep";
import { WizardResult } from "./WizardResult";
const BASE_SHADOW = "0 25px 50px -12px rgba(0,0,0,0.4)";
const GLOW_SHADOW = `0 0 0 1px rgba(255,183,0,0.85), 0 0 36px rgba(255,183,0,0.3), ${BASE_SHADOW}`;
const REST_SHADOW = `0 0 0 1px rgba(255,183,0,0), 0 0 0px rgba(255,183,0,0), ${BASE_SHADOW}`;
const STEP_EASE = [0.22, 1, 0.36, 1] as const;
const FOCUSABLE = 'input:not([type="hidden"]), button[role="radio"]';

/** Always visible — never wrapped in a scroll reveal. */
export function ApplicationWizard() {
  const w = useApplicationWizard();
  const reduce = useReducedMotion();
  const glow = useAnimationControls();
  const stepRef = useRef<HTMLDivElement>(null);
  const firstView = useRef(true);
  const done = w.status === "qualified" || w.status === "not_qualified";
  const progress = done ? 100 : (w.step + 1) / TOTAL_STEPS * 100;
  const current = wizardSteps[w.step];
  const question = current?.kind === "question" ? current.question : null;
  const viewKey = done ? w.status : `step-${w.step}`;
  useEffect(() => {
    const onArrive = (e: Event) => {
      if (reduce) return;
      const {
        jumped
      } = (e as CustomEvent<ApplyArrivedDetail>).detail ?? {
        jumped: false
      };
      if (jumped) {
        glow.set({
          opacity: 0,
          boxShadow: GLOW_SHADOW
        });
        glow.start({
          opacity: 1,
          boxShadow: REST_SHADOW,
          transition: {
            opacity: {
              duration: 0.2,
              ease: "easeOut"
            },
            boxShadow: {
              duration: 1.2,
              ease: "easeOut"
            }
          }
        });
        return;
      }
      glow.set({
        boxShadow: GLOW_SHADOW
      });
      glow.start({
        boxShadow: REST_SHADOW,
        transition: {
          duration: 1.2,
          ease: "easeOut"
        }
      });
    };
    window.addEventListener(APPLY_ARRIVED_EVENT, onArrive);
    return () => window.removeEventListener(APPLY_ARRIVED_EVENT, onArrive);
  }, [glow, reduce]);

  // Focus the first option/input of each new step (not on initial page load).
  useEffect(() => {
    if (firstView.current) {
      firstView.current = false;
      return;
    }
    if (done) return;
    const t = window.setTimeout(() => {
      stepRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus({
        preventScroll: true
      });
    }, 200);
    return () => window.clearTimeout(t);
  }, [viewKey, done]);
  return <section id={APPLY_SECTION_ID} aria-labelledby="aplicar-titulo" className="section-pad border-b border-line bg-surface" data-id="element-22" data-mp="d27s6m7qjw8m">
      <div className="page-container" data-id="element-23" data-mp="1xjz3xs17igaj">
        <div className="mx-auto max-w-[720px]" data-id="element-24" data-mp="1slfgtoavmygl">
          <SectionHeader number="10" label="Aplicación" titleId="aplicar-titulo" title="Aplica a tu diagnóstico gratis" data-id="element-25" data-mp="1smqjfksl6064" />
          <p className="mt-3 text-[14px] text-muted" data-id="element-26" data-mp="1iwe2s3nw6r59">{CTA_MICROCOPY}</p>

          <motion.div animate={glow} initial={{
          opacity: 1,
          boxShadow: REST_SHADOW
        }} className="-mx-6 mt-8 border-y border-lineStrong bg-ink md:mx-0 md:mt-12 md:rounded-[3px] md:border" data-id="element-27" data-mp="1dwjbw044a1k7">
            <div className="sticky top-16 z-10 bg-ink px-6 pb-3 pt-4 md:static md:px-10 md:pb-0 md:pt-8" data-id="element-28" data-mp="14ww8j8rtuloi">
              <div className="flex h-6 items-center justify-between gap-4" data-id="element-29" data-mp="zy1rab9id56i">
                <span className="eyebrow tabular-nums" data-id="element-30" data-mp="2zadvouoblgj0">
                  {done ? "Solicitud completa" : `Paso ${w.step + 1} de ${TOTAL_STEPS}`}
                </span>
                {!done && w.step > 0 && <button type="button" onClick={w.back} className="-my-3 -mr-2 inline-flex min-h-[48px] items-center gap-1.5 px-2 font-ui text-[13px] font-bold text-muted underline-offset-4 transition-colors duration-200 ease-out hover:text-cream hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-31" data-mp="1ryekecgrmyk8">
                    <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-32" data-mp="187t2v474xfxn" />
                    Atrás
                  </button>}
              </div>
              <div className="mt-4 h-px w-full bg-lineStrong" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)} aria-label="Progreso de la solicitud" data-id="element-33" data-mp="z9h2wbkdp5sj">
                <motion.div className="h-[2px] -translate-y-[0.5px] bg-gold" initial={false} animate={{
                width: `${progress}%`
              }} transition={{
                duration: reduce ? 0 : 0.18,
                ease: STEP_EASE
              }} data-id="element-34" data-mp="u9ussazuk5ny" />
              </div>
            </div>

            <div ref={stepRef} className="px-6 pb-8 pt-7 md:px-10 md:pb-10 md:pt-10" data-id="element-35" data-mp="kms4ye5rz0o9">
              <AnimatePresence mode="wait" initial={false} data-id="element-36" data-mp="flunk3slaant">
                <motion.div key={viewKey} initial={reduce ? {
                opacity: 0
              } : {
                opacity: 0,
                x: 8
              }} animate={{
                opacity: 1,
                x: 0,
                transition: {
                  duration: 0.18,
                  ease: STEP_EASE
                }
              }} exit={{
                opacity: 0,
                transition: {
                  duration: 0.06,
                  ease: "easeOut"
                }
              }} data-id="element-37" data-mp="tmnkswa9zjum">
                  {done ? <WizardResult qualified={w.status === "qualified"} contact={w.contact} data-id="element-38" data-mp="ye5jtesqnacy" /> : question ? <WizardOptionStep question={question} selected={w.answers[question.key]} onSelect={v => w.selectOption(question.key, v)} data-id="element-39" data-mp="18bjo6g1e08ow" /> : <WizardContactForm contact={w.contact} errors={w.errors} onChange={w.updateContact} onSubmit={w.submitContact} data-id="element-40" data-mp="1kedznhooll49" />}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}